import { createContext, FC, HTMLAttributes, ReactNode, useContext } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";

import { AutocompleteFooter } from "./AutocompleteFooter/AutocompleteFooter";
import { AutocompleteFooterConfig, AutocompleteOptionSize } from "./types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox/DashboardCheckbox";
import { DashboardDivider } from "@/dashboard-design-system/Divider";

interface AutocompleteV2PaperContextValue {
  selectAll: boolean;
  multiple: boolean;
  allSelected: boolean;
  selectAllLabel: string;
  optionSize: AutocompleteOptionSize;
  onSelectAll: () => void;
  onApply: () => void;
  onClear: () => void;
  footerConfig?: AutocompleteFooterConfig;
  paperSx?: SxProps<Theme>;
  listboxSx?: SxProps<Theme>;
}

const AutocompleteV2PaperContext = createContext<AutocompleteV2PaperContextValue | null>(null);

const useAutocompleteV2PaperContext = () => {
  const ctx = useContext(AutocompleteV2PaperContext);
  if (!ctx) {
    throw new Error("AutocompleteV2Paper must be used within AutocompleteV2PaperProvider");
  }
  return ctx;
};

interface AutocompleteV2PaperProviderProps extends AutocompleteV2PaperContextValue {
  children: ReactNode;
}

export const AutocompleteV2PaperProvider: FC<AutocompleteV2PaperProviderProps> = ({ children, ...value }) => (
  <AutocompleteV2PaperContext.Provider value={value}>{children}</AutocompleteV2PaperContext.Provider>
);

const preventBlur = (e: React.MouseEvent) => e.preventDefault();

export const AutocompleteV2Paper = ({ children, ...paperProps }: HTMLAttributes<HTMLElement>) => {
  const {
    selectAll,
    multiple,
    allSelected,
    selectAllLabel,
    optionSize,
    onSelectAll,
    onApply,
    onClear,
    footerConfig,
    paperSx,
    listboxSx,
  } = useAutocompleteV2PaperContext();

  const isSmall = optionSize === "small";

  return (
    <Box
      {...paperProps}
      onMouseDown={preventBlur}
      sx={mergeSx(
        (theme: Theme) => ({
          backgroundColor: theme.palette.surface.neutral.white,
          borderRadius: theme.radius(1),
          boxShadow: theme.boxShadow.base.mid,
          overflow: "hidden",
        }),
        paperSx
      )}
    >
      {selectAll && multiple && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            onClick={onSelectAll}
            sx={(theme: Theme) => ({
              padding: theme.spacing(2, 2, 1, 2),
              cursor: "pointer",
              backgroundColor: allSelected ? theme.palette.surface.brand.quiet : "transparent",
              "&:hover": { backgroundColor: theme.palette.surface.brand.quiet },
            })}
          >
            <DashboardCheckbox
              checked={allSelected}
              size={isSmall ? "small" : "medium"}
              sx={{ padding: 0, marginRight: 1 }}
              tabIndex={-1}
            />
            <Box
              sx={(theme: Theme) => ({
                ...(isSmall ? theme.typography.body2 : theme.typography.body1),
                color: theme.palette.text.neutral.moderate,
              })}
            >
              {selectAllLabel}
            </Box>
          </Stack>
          <DashboardDivider />
        </Box>
      )}

      <Box
        sx={mergeSx(
          (theme: Theme) => ({
            overflowY: "auto",
            overflowX: "hidden",
            maxHeight: 240,
            "&::-webkit-scrollbar": { width: 14 },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.surface.neutral.supershy,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.border.neutral.moderate,
              borderRadius: 4,
              border: "4px solid transparent",
              backgroundClip: "content-box",
            },
            scrollbarWidth: "thin",
            "& ul": { padding: 0, margin: 0 },
          }),
          listboxSx
        )}
      >
        {children}
      </Box>

      {footerConfig && (
        <AutocompleteFooter
          {...(footerConfig.type === "default"
            ? { ...footerConfig, onApply, onClear, size: optionSize }
            : { ...footerConfig, size: optionSize })}
        />
      )}
    </Box>
  );
};

AutocompleteV2Paper.displayName = "AutocompleteV2Paper";
